// Static Scroll Content - Simplified version for static image + scrolling text
/**
 * @typedef {HTMLElement & { staticScrollContent?: StaticScrollContent | null }} StaticScrollContentHost
 */
class StaticScrollContent {
  /** @param {HTMLElement} element */
  constructor(element) {
    /** @type {StaticScrollContentHost} */
    this.element = /** @type {StaticScrollContentHost} */ (element);
    /** @type {NodeListOf<HTMLElement>} */
    this.features = element.querySelectorAll('.static-scroll-content__feature');
    this.currentFeatureIndex = -1;
    this.isScrolling = false;
    /** @type {null | (() => void)} */
    this._cleanupScroll = null;
    /** @type {{ top: number; height: number }[]} */
    this.featurePositions = [];
    /** @type {AbortController} */
    this._controller = new AbortController();
    
    if (this.features.length === 0) {
      return;
    }
    
    this.init();
  }

  init() {
    // expose instance for theme editor unload cleanup
    this.element.staticScrollContent = this;

    this.isMobile = window.innerWidth <= 775;
    this.sectionStart = 0;
    this.sectionEnd = 0;
    this.frameCount = this.features.length;
    this.lastFeatureIndex = -1;
    this.featureChangeThreshold = 0.1; // Require 10% progress change before switching features
    
    // Get reference to features wrapper and container for size reduction when all features shown
    this.featuresWrapper = this.element.querySelector('.static-scroll-content__features-wrapper');
    this.featuresContainer = this.element.querySelector('.static-scroll-content__features');
    
    this.cacheFeaturePositions();
    this.measureSection();
    this.setupScrollHandler();
    this.setupClickHandlers();
    this.updateFeatureState(0);
    this.updateScrollPosition(0);
    
    // Handle resize/orientation change
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth <= 775;
      this.measureSection();
      this.updateFeatureState(this.currentFeatureIndex);
    }, { passive: true });
  }
  
  measureSection() {
    const rect = this.element.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
    
    // Section start is when the section top reaches the viewport top (when sticky starts)
    // This happens when rect.top <= 0 (section top is at or above viewport top)
    this.sectionStart = scrollY + rect.top;
    
    if (this.isMobile) {
      // Mobile: map scroll progress over feature transition range (frameCount * 50vh)
      // The section itself is taller (frameCount * 50vh + 100vh) to keep sticky container in place
      // But feature transitions only happen over the frameCount * 50vh range
      const featureScrollHeight = viewportHeight * this.frameCount * 0.5; // 50vh per feature
      this.sectionEnd = this.sectionStart + featureScrollHeight;
    } else {
      // Desktop: feature changes happen over 50vh per feature
      // The section is taller (100vh + 50vh*frameCount) to allow sticky to work
      // But feature changes only happen over the 50vh*frameCount range
      const featureScrollHeight = viewportHeight * this.frameCount * 0.5; // 50vh per feature
      // Start feature changes when sticky container starts sticking (section top at viewport top)
      this.sectionEnd = this.sectionStart + featureScrollHeight;
    }
  }

  cacheFeaturePositions() {
    /** @type {HTMLElement | null} */
    const featuresContainer = /** @type {HTMLElement | null} */ (
      this.element.querySelector('.static-scroll-content__features')
    );
    if (!featuresContainer) {
      return;
    }
    
    // Reset transform to get natural positions
    const originalTransform = featuresContainer.style.transform;
    featuresContainer.style.transform = 'translateY(0)';
    void featuresContainer.offsetHeight; // Force reflow
    
    // Get gap value
    const gapStyle = getComputedStyle(featuresContainer).gap || '0px';
    const gapMatch = gapStyle.match(/([\d.]+)(px|rem|em)/);
    let gap = 0;
    if (gapMatch) {
      const gapValue = parseFloat(gapMatch?.[1] ?? '0');
      const unit = gapMatch?.[2] ?? 'px';
      if (unit === 'rem') {
        gap = gapValue * parseFloat(getComputedStyle(document.documentElement).fontSize);
      } else if (unit === 'em') {
        gap = gapValue * parseFloat(getComputedStyle(featuresContainer).fontSize);
      } else {
        gap = gapValue;
      }
    }
    
    // Cache positions
    this.featurePositions = [];
    let cumulativeTop = 0;
    this.features.forEach((feature, index) => {
      this.featurePositions.push({
        top: cumulativeTop,
        height: feature.offsetHeight,
      });
        // Add gap after this feature (except for the last one)
        if (index < this.features.length - 1) {
          cumulativeTop += feature.offsetHeight + gap;
        } else {
          cumulativeTop += feature.offsetHeight;
        }
    });
    
    // Restore transform
    featuresContainer.style.transform = originalTransform;
  }

  setupScrollHandler() {
    let ticking = false;

    const updateByScroll = () => {
      // Always measure section, but skip feature updates during programmatic scrolling
      this.measureSection();
      
      if (this.isScrolling) return;

      // Both mobile and desktop use scroll-driven progress for smooth transitions
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const scrollRange = this.sectionEnd - this.sectionStart;
      
      // Prevent division by zero
      if (scrollRange <= 0) {
        this.updateFeatureState(0, 0);
        return;
      }
      
      const raw = (scrollY - this.sectionStart) / scrollRange;
      const progress = Math.max(0, Math.min(1, raw));
      
      // Map progress to feature index (0 to frameCount - 1)
      // Each feature gets an equal portion of the scroll range
      // Use smooth calculation: feature 0 = 0 to 1/frameCount, feature 1 = 1/frameCount to 2/frameCount, etc.
      // Ensure we don't exceed bounds
      const featureProgress = progress * this.frameCount;
      let featureIndex = Math.max(0, Math.min(
        Math.floor(featureProgress),
        this.features.length - 1
      ));
      
      // Ensure we always show the last feature when we've reached the end
      // If progress is very close to 1, make sure we're on the last feature
      if (progress >= 0.99) {
        featureIndex = this.features.length - 1;
      }
      
      this.updateFeatureState(featureIndex, progress);
      this.updateScrollPosition(featureIndex);
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateByScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    this._cleanupScroll = () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };

    updateByScroll();
  }

  /** @param {number} featureIndex @param {number} [progress] */
  updateScrollPosition(featureIndex, progress = 0) {
    // Features should stay in their natural position below the heading
    // No transform needed - features remain static
    /** @type {HTMLElement | null} */
    const featuresContainer = /** @type {HTMLElement | null} */ (
      this.element.querySelector('.static-scroll-content__features')
    );
    if (featuresContainer) {
      featuresContainer.style.transform = 'translateY(0)';
    }
  }

  setupClickHandlers() {
    const { signal } = this._controller;
    this.features.forEach((feature) => {
      feature.addEventListener(
        'click',
        () => {
          this.scrollToFeature(feature);
        },
        { signal }
      );

      /** @param {KeyboardEvent} event */
      const onKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.scrollToFeature(feature);
        }
      };

      feature.addEventListener('keydown', onKeyDown, { signal });
    });
  }

  /** @param {HTMLElement} feature */
  scrollToFeature(feature) {
    const rect = feature.getBoundingClientRect();
    const viewportCenter = window.innerHeight / 2;
    const scrollOffset = rect.top + rect.height / 2 - viewportCenter;
    this.isScrolling = true;
    
    const featureIndex = parseInt(feature.dataset.featureIndex ?? '', 10);
    if (!Number.isNaN(featureIndex)) {
      // Calculate progress based on feature index
      const progress = featureIndex / this.frameCount;
      this.updateFeatureState(featureIndex, progress);
      this.updateScrollPosition(featureIndex);
    }
    
    window.scrollBy({ top: scrollOffset, behavior: 'smooth' });
    
    setTimeout(() => {
      this.isScrolling = false;
    }, 1000);
  }

  /**
   * Calculate available space for content on mobile
   * @returns {{availableSpace: number, headerHeight: number, animationHeight: number, viewportHeight: number, viewportCategory: string}}
   */
  calculateAvailableSpace() {
    const viewportHeight = window.innerHeight;
    const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
    const animationHeight = viewportHeight * 0.6; // 60vh
    const availableSpace = viewportHeight - animationHeight - headerHeight;
    
    // Categorize viewport height
    let viewportCategory = 'large';
    if (viewportHeight < 700) {
      viewportCategory = 'small';
    } else if (viewportHeight < 850) {
      viewportCategory = 'medium';
    }
    
    return {
      availableSpace,
      headerHeight,
      animationHeight,
      viewportHeight,
      viewportCategory
    };
  }

  /** @param {number} featureIndex @param {number} [progress] */
  updateFeatureState(featureIndex, progress = 0) {
    // Always update to ensure smooth transitions, even if index hasn't changed
    // (progress might have changed, affecting visual state)
    this.currentFeatureIndex = featureIndex;
    
    if (this.isMobile && this.features.length > 0) {
      // Mobile: Stacked effect - active on top, previous stacked behind, next preview
      const effectiveActive = featureIndex >= 0 ? featureIndex : 0;
      const space = this.calculateAvailableSpace();
      
      // Calculate if we can show previews based on available space
      const activeFeature = this.features[effectiveActive];
      const activeFeatureHeight = activeFeature ? activeFeature.offsetHeight : 80;
      const previewHeight = 40; // Approximate preview height
      const padding = 20;
      const canShowPreviews = space.availableSpace > (activeFeatureHeight + previewHeight + padding);
      const canShowSecondPreview = canShowPreviews && space.availableSpace > (activeFeatureHeight + (previewHeight * 2) + padding);
      
      // Check if all features have been shown
      const lastFeatureIndex = this.features.length - 1;
      // Trigger when we're on the last feature and have scrolled a bit into it
      // This ensures the last feature is fully visible before shrinking
      const allShown = effectiveActive >= lastFeatureIndex && progress >= 0.75;
      
      // Add class to reduce features wrapper/container size when all features are shown
      if (this.featuresWrapper) {
        if (allShown) {
          this.featuresWrapper.classList.add('static-scroll-content__features-wrapper--all-shown');
        } else {
          this.featuresWrapper.classList.remove('static-scroll-content__features-wrapper--all-shown');
        }
      }
      if (this.featuresContainer) {
        if (allShown) {
          this.featuresContainer.classList.add('static-scroll-content__features--all-shown');
        } else {
          this.featuresContainer.classList.remove('static-scroll-content__features--all-shown');
        }
      }
      
      this.features.forEach((feature, index) => {
        const isActive = index === effectiveActive;
        const isNext = index === effectiveActive + 1;
        const isPreview = index === effectiveActive + 2; // Second next feature
        const isPrevious = index < effectiveActive;
        const isFarPast = index < effectiveActive - 1; // Features that are 1+ positions behind
        const isLastFeature = index === lastFeatureIndex;

        // Remove all classes first
        feature.classList.remove(
          'static-scroll-content__feature--active',
          'is-past',
          'is-far-past',
          'is-adjacent',
          'is-preview'
        );

        // Clear inline styles
        feature.style.opacity = '';
        feature.style.pointerEvents = '';

        if (isActive) {
          // Active feature: fully visible on top
          feature.classList.add('static-scroll-content__feature--active');
        } else if (isNext) {
          // Next feature: preview behind active
          // Always show the next feature (especially important for the last feature)
          feature.classList.add('is-adjacent');
        } else if (isPreview && canShowSecondPreview && !allShown) {
          // Second next feature: subtle preview (only if space allows)
          feature.classList.add('is-preview');
        } else if (isPrevious) {
          // Previous features: stacked behind, faded
          feature.classList.add('is-past');
          if (isFarPast) {
            feature.classList.add('is-far-past');
          }
        } else {
          // Future features: hidden (but never hide the last feature)
          if (!isLastFeature) {
            feature.style.opacity = '0';
            feature.style.pointerEvents = 'none';
          } else {
            // Last feature should always be visible, show as adjacent if not active
            feature.classList.add('is-adjacent');
          }
        }
      });
    } else {
      // Desktop: Show all features, just toggle active state
      this.features.forEach((feature, index) => {
        if (index === featureIndex) {
          feature.classList.add('static-scroll-content__feature--active');
        } else {
          feature.classList.remove('static-scroll-content__feature--active');
        }
        // Remove mobile-specific classes on desktop
        feature.classList.remove('is-past', 'is-far-past', 'is-adjacent', 'is-preview');
        feature.style.opacity = '';
        feature.style.pointerEvents = '';
      });
      
      // Remove the all-shown classes on desktop (mobile-only feature)
      if (this.featuresWrapper) {
        this.featuresWrapper.classList.remove('static-scroll-content__features-wrapper--all-shown');
      }
      if (this.featuresContainer) {
        this.featuresContainer.classList.remove('static-scroll-content__features--all-shown');
      }
    }
  }

  destroy() {
    this._controller.abort();
    if (this._cleanupScroll) this._cleanupScroll();
    this.element.staticScrollContent = null;
  }
}

// Auto-init
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.static-scroll-content');
  sections.forEach((el) => new StaticScrollContent(/** @type {HTMLElement} */ (el)));
});

// Re-initialize on theme editor changes
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const root = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
    /** @type {StaticScrollContentHost | null} */
    const section = /** @type {StaticScrollContentHost | null} */ (root?.querySelector('.static-scroll-content') ?? null);
    if (section) {
      new StaticScrollContent(/** @type {HTMLElement} */ (section));
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const root = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
    /** @type {StaticScrollContentHost | null} */
    const section = /** @type {StaticScrollContentHost | null} */ (root?.querySelector('.static-scroll-content') ?? null);
    if (section && section.staticScrollContent) {
      section.staticScrollContent.destroy();
    }
  });
}

export {};
